import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { ExaminationSubmittedPopupComponent } from '../../components/examination-submitted-popup/examination-submitted-popup';
import { ExamResultState } from '../../model/test-series.model';
import { AuthService } from '../../../auth/services/auth.service';
import { UserProfileData } from '../../../auth/model/auth.model';
import { getReviewLockedMessage } from '../../../../shared/utils/result-release.util';

@Component({
  selector: 'app-examination-submitted',
  imports: [ExaminationSubmittedPopupComponent],
  templateUrl: './examination-submitted.html',
  styleUrl: './examination-submitted.css',
})
export class ExaminationSubmittedComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  protected readonly userProfile = signal<UserProfileData | null>(null);
  protected readonly defaultReviewLockedMessage = getReviewLockedMessage(null, null);

  private readonly defaultExamResultState: ExamResultState = {
    examTitle: 'National NEET-PG Mock 2024 (New NEET-PG Pattern)',
    examMeta: 'Full Mock Test · Jul 16, 2026',
    submittedAt: new Date().toISOString(),
    candidateName: this.getCandidateDisplayName(),
    candidateInitials: this.getCandidateInitials(),
    performanceLabel: 'Great Performance!',
    submissionSummary: {
      totalQuestions: 200,
      totalAttempted: 36,
      totalUnattempted: 164,
      markedForReview: 8,
      timeTaken: '3h 27m',
      remainingTime: '71 min',
    },
    sectionSummaries: [],
  };

  protected examResultState =
    {
      ...((history.state as { examResultState?: ExamResultState }).examResultState ?? this.defaultExamResultState),
      candidateName: (history.state as { examResultState?: ExamResultState }).examResultState?.candidateName || this.getCandidateDisplayName(),
      candidateInitials: (history.state as { examResultState?: ExamResultState }).examResultState?.candidateInitials || this.getCandidateInitials(),
    };

  ngOnInit(): void {
    void this.exitFullscreenIfActive();

    this.authService.getUserDetails().subscribe({
      next: (res) => {
        if (res?.data) {
          this.userProfile.set(res.data);
          this.examResultState = {
            ...this.examResultState,
            candidateName: this.getCandidateDisplayName(),
            candidateInitials: this.getCandidateInitials(),
          };
        }
      },
      error: () => {
        // Keep fallback state if profile fetch fails.
      },
    });
  }

  protected onBackToDashboard(): void {
    void this.navigateAfterExitingFullscreen(['/dashboard']);
  }

  protected onViewResult(): void {
    const testId = this.route.snapshot.paramMap.get('id');

    if (!testId) {
      void this.navigateAfterExitingFullscreen(['/test-series']);
      return;
    }

    void this.navigateAfterExitingFullscreen(['/test-series', testId, 'exam-summary'], {
      state: { examResultState: this.examResultState },
      replaceUrl: true,
    });
  }

  private getCandidateDisplayName(): string {
    const name = this.userProfile()?.name?.trim() ?? '';
    return name || 'User';
  }

  private getCandidateInitials(): string {
    const nameParts = this.getCandidateDisplayName().split(/\s+/).filter(Boolean);

    if (!nameParts.length) {
      return 'U';
    }

    return `${nameParts[0]?.charAt(0) ?? ''}${nameParts[1]?.charAt(0) ?? ''}`.toUpperCase();
  }

  private async navigateAfterExitingFullscreen(commands: string[], extras?: NavigationExtras): Promise<void> {
    await this.exitFullscreenIfActive();
    await this.router.navigate(commands, extras);
  }

  private async exitFullscreenIfActive(): Promise<void> {
    if (!document.fullscreenElement || !document.exitFullscreen) {
      return;
    }

    try {
      await document.exitFullscreen();
    } catch {
      // Keep the submitted screen accessible even if the browser blocks fullscreen exit here.
    }
  }
}
